import React, { useEffect, useState } from "react";
import OuraNav from "../components/OuraNav";
import fetchBase from "../utils/fetchBase";

export default function AdminProfilePage() {
    const authHeader = "Bearer " + localStorage.getItem("jwt_token");

    const [profile, setProfile] = useState(null);
    const [billingDraft, setBillingDraft] = useState({ billingLegalName: "", npi: "", taxId: "", phone: "" });
    const [saveStatus, setSaveStatus] = useState(null);

    const [clients, setClients] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [insuranceDraft, setInsuranceDraft] = useState({ insuranceMemberId: "", payerId: "", primaryIcd10Code: "", vaAuthorizationNumber: "", dateOfBirth: "" });
    const [confirmDraft, setConfirmDraft] = useState({ insuranceMemberId: "", vaAuthorizationNumber: "" });
    const [payerMatch, setPayerMatch] = useState(null);

    const lookupPayer = async () => {
        const query = insuranceDraft.payerId?.trim();
        if (!query) { setPayerMatch(null); return; }
        try {
            const res = await fetch(`${fetchBase}/api/billing/payer-lookup?query=${encodeURIComponent(query)}`, {
                headers: { Authorization: authHeader }
            });
            const data = await res.json();
            const items = data.items || data.results || [];
            const exact = items.find(i =>
                i.payer?.primaryPayerId === query || (i.payer?.aliases || []).includes(query)
            );
            if (exact) {
                setPayerMatch({
                    exact: true,
                    name: exact.payer.displayName,
                    claimsSupported: exact.payer.transactionSupport?.professionalClaimSubmission === "SUPPORTED"
                });
            } else if (items.length > 0) {
                setPayerMatch({ exact: false, name: items[0].payer?.displayName, closestId: items[0].payer?.primaryPayerId });
            } else {
                setPayerMatch({ exact: false, name: null });
            }
        } catch (err) {
            setPayerMatch(null);
        }
    };

    useEffect(() => {
        fetch(`${fetchBase}/api/admin/profile`, { headers: { Authorization: authHeader } })
            .then(res => res.json())
            .then(data => {
                setProfile(data);
                setBillingDraft({
                    billingLegalName: data.billingLegalName || "",
                    npi: data.npi || "",
                    taxId: data.taxId || "",
                    phone: data.phone || ""
                });
            })
            .catch(err => console.error("Failed to fetch profile:", err));

        fetch(`${fetchBase}/api/client/by-admin`, { headers: { Authorization: authHeader } })
            .then(res => res.json())
            .then(data => { if (Array.isArray(data)) setClients(data); })
            .catch(err => console.error("Failed to fetch clients:", err));

        fetch(`${fetchBase}/api/admin/by-admin`, { headers: { Authorization: authHeader } })
            .then(res => res.json())
            .then(data => { if (Array.isArray(data)) setEmployees(data); })
            .catch(err => console.error("Failed to fetch employees:", err));
    }, []);

    const saveBilling = async () => {
        setSaveStatus("saving");
        const res = await fetch(`${fetchBase}/api/admin/profile/billing`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", Authorization: authHeader },
            body: JSON.stringify(billingDraft)
        });
        setSaveStatus(res.ok ? "saved" : "error");
        if (res.ok) setTimeout(() => setSaveStatus(null), 1500);
    };

    const openClientModal = (client) => {
        setSelectedClient(client);
        setInsuranceDraft({
                    insuranceMemberId: client.insuranceMemberId || "",
                    payerId: client.payerId || "",
                    primaryIcd10Code: client.primaryIcd10Code || "",
                    vaAuthorizationNumber: client.vaAuthorizationNumber || "",
                    dateOfBirth: client.dateOfBirth || ""
                });
                setConfirmDraft({
                    insuranceMemberId: client.insuranceMemberId || "",
                    vaAuthorizationNumber: client.vaAuthorizationNumber || ""
                });
    };

    const saveInsurance = async () => {
        const res = await fetch(`${fetchBase}/api/client/${selectedClient.id}/insurance`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", Authorization: authHeader },
            body: JSON.stringify(insuranceDraft)
        });
        if (res.ok) {
            setClients(prev => prev.map(c => c.id === selectedClient.id ? { ...c, ...insuranceDraft } : c));
            setSelectedClient(null);
        } else {
            alert("Failed to save insurance info.");
        }
    };

    const inputStyle = { width: "100%", padding: "8px 10px", fontSize: "13px", border: "1px solid #ccc", borderRadius: "8px", marginTop: "4px" };
    const labelStyle = { fontSize: "12px", fontWeight: "600", color: "#1d1d1f", display: "block", marginBottom: "12px" };

    return (
        <div style={{ minHeight: "100vh", background: "#f5f5f7" }}>
            <OuraNav links={[{ label: "Dashboard", href: "/admin-dashboard" }]} />
            <div style={{ maxWidth: "800px", margin: "0 auto", padding: "80px 20px 40px" }}>

                <h1 style={{ fontSize: "28px", fontWeight: "700", color: "#1d1d1f", marginBottom: "24px" }}>Profile</h1>

                {profile && (
                    <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: "14px", padding: "24px", marginBottom: "30px" }}>
                        <p style={{ fontSize: "14px", color: "#6e6e73", margin: "0 0 4px" }}>Email</p>
                        <p style={{ fontSize: "15px", fontWeight: "600", color: "#1d1d1f", margin: "0 0 16px" }}>{profile.email}</p>
                        <p style={{ fontSize: "14px", color: "#6e6e73", margin: "0 0 4px" }}>Company</p>
                        <p style={{ fontSize: "15px", fontWeight: "600", color: "#1d1d1f", margin: "0 0 20px" }}>{profile.organizationName}</p>

                        <label style={labelStyle}>
                            Billing Legal Name
                            <input style={inputStyle} value={billingDraft.billingLegalName}
                                onChange={e => setBillingDraft(prev => ({ ...prev, billingLegalName: e.target.value }))} />
                        </label>
                        <label style={labelStyle}>
                            NPI
                            <input style={inputStyle} value={billingDraft.npi}
                                onChange={e => setBillingDraft(prev => ({ ...prev, npi: e.target.value }))} />
                        </label>
                        <label style={labelStyle}>
                            Tax ID
                            <input style={inputStyle} value={billingDraft.taxId}
                                onChange={e => setBillingDraft(prev => ({ ...prev, taxId: e.target.value }))} />
                        </label>
                        <label style={labelStyle}>
                            Company Number <span style={{ fontWeight: 400, fontSize: "11px", color: "#a1a1a6" }}>e.g. 5552221212 — 10 digits, no dashes or country code</span>
                            <input style={inputStyle} value={billingDraft.phone}
                                onChange={e => setBillingDraft(prev => ({ ...prev, phone: e.target.value }))} />
                        </label>

                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <button onClick={saveBilling}
                                style={{ background: "#0071e3", color: "#fff", border: "none", borderRadius: "8px", padding: "8px 18px", fontWeight: 600, fontSize: "13px", cursor: "pointer" }}>
                                Save
                            </button>
                            {saveStatus && (
                                <span style={{ fontSize: "12px", fontWeight: 600, color: saveStatus === "error" ? "#ff3b30" : "#6e6e73" }}>
                                    {saveStatus === "saving" ? "Saving..." : saveStatus === "saved" ? "Saved" : "Save failed"}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                <h2 style={{ fontSize: "18px", fontWeight: "600", color: "#1d1d1f", margin: "0 0 14px" }}>VA Billing</h2>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "14px", marginBottom: "30px" }}>
                    {clients.filter(c => c.clientType === "VA").map(c => (
                        <div key={c.id} onClick={() => openClientModal(c)}
                            style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: "12px", padding: "16px", cursor: "pointer", textAlign: "center" }}>
                            <p style={{ fontWeight: "600", fontSize: "14px", color: "#1d1d1f", margin: 0 }}>{c.firstName} {c.lastName}</p>
                        </div>
                    ))}
                </div>

                <h2 style={{ fontSize: "18px", fontWeight: "600", color: "#1d1d1f", margin: "0 0 14px" }}>Employees</h2>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "14px" }}>
                    {employees.map(e => (
                        <div key={e.id}
                            style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: "12px", padding: "16px" }}>
                            <p style={{ fontWeight: "600", fontSize: "14px", color: "#1d1d1f", margin: "0 0 4px" }}>{e.firstName} {e.lastName}</p>
                            <p style={{ fontSize: "12px", color: "#6e6e73", margin: "0 0 2px" }}>{e.position}</p>
                            <p style={{ fontSize: "12px", color: "#6e6e73", margin: "0 0 2px" }}>{e.phone}</p>
                            <p style={{ fontSize: "12px", color: "#6e6e73", margin: 0 }}>{e.email}</p>
                        </div>
                    ))}
                </div>

                {selectedClient && (
                    <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
                        onClick={() => setSelectedClient(null)}>
                        <div style={{ background: "#fff", borderRadius: "14px", padding: "24px", width: "380px" }} onClick={e => e.stopPropagation()}>
                            <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#1d1d1f", margin: "0 0 16px" }}>
                                {selectedClient.firstName} {selectedClient.lastName} — Insurance
                            </h3>
                            <label style={labelStyle}>
                                Date of Birth
                                <input type="date" style={inputStyle} value={insuranceDraft.dateOfBirth}
                                    onChange={e => setInsuranceDraft(prev => ({ ...prev, dateOfBirth: e.target.value }))} />
                            </label>
                            <label style={labelStyle}>
                                Insurance Member ID
                                <input style={inputStyle} value={insuranceDraft.insuranceMemberId}
                                    onChange={e => setInsuranceDraft(prev => ({ ...prev, insuranceMemberId: e.target.value }))} />
                            </label>
                            <label style={labelStyle}>
                                Confirm Insurance Member ID
                                <input style={{ ...inputStyle, borderColor: confirmDraft.insuranceMemberId === insuranceDraft.insuranceMemberId ? "#34c759" : "#ff3b30" }}
                                    value={confirmDraft.insuranceMemberId}
                                    onChange={e => setConfirmDraft(prev => ({ ...prev, insuranceMemberId: e.target.value }))} />
                                {confirmDraft.insuranceMemberId !== insuranceDraft.insuranceMemberId && (
                                    <span style={{ fontSize: "11px", color: "#ff3b30" }}>Member IDs do not match</span>
                                )}
                            </label>
                            <label style={labelStyle}>
                                Payer ID
                                <input style={inputStyle} value={insuranceDraft.payerId}
                                    onChange={e => { setInsuranceDraft(prev => ({ ...prev, payerId: e.target.value })); setPayerMatch(null); }}
                                    onBlur={lookupPayer} />
                                {payerMatch && payerMatch.exact && (
                                    <span style={{ fontSize: "11px", color: "#34c759" }}>
                                        ✓ {payerMatch.name}{payerMatch.claimsSupported === false ? " — does not accept professional claims" : ""}
                                    </span>
                                )}
                                {payerMatch && !payerMatch.exact && (
                                    <span style={{ fontSize: "11px", color: "#6e6e73" }}>
                                        {payerMatch.name ? `No exact match — closest: ${payerMatch.name} (${payerMatch.closestId})` : "No matching payer found"}
                                    </span>
                                )}
                            </label>
                            <label style={labelStyle}>
                                Primary ICD-10 Code
                                <input style={inputStyle} value={insuranceDraft.primaryIcd10Code}
                                    onChange={e => setInsuranceDraft(prev => ({ ...prev, primaryIcd10Code: e.target.value }))} />
                            </label>
                            <label style={labelStyle}>
                                VA Authorization Number
                                <input style={inputStyle} value={insuranceDraft.vaAuthorizationNumber}
                                    onChange={e => setInsuranceDraft(prev => ({ ...prev, vaAuthorizationNumber: e.target.value }))} />
                            </label>
                            <label style={labelStyle}>
                                Confirm VA Authorization Number
                                <input style={{ ...inputStyle, borderColor: confirmDraft.vaAuthorizationNumber === insuranceDraft.vaAuthorizationNumber ? "#34c759" : "#ff3b30" }}
                                    value={confirmDraft.vaAuthorizationNumber}
                                    onChange={e => setConfirmDraft(prev => ({ ...prev, vaAuthorizationNumber: e.target.value }))} />
                                {confirmDraft.vaAuthorizationNumber !== insuranceDraft.vaAuthorizationNumber && (
                                    <span style={{ fontSize: "11px", color: "#ff3b30" }}>Authorization numbers do not match</span>
                                )}
                            </label>
                            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                                <button onClick={() => setSelectedClient(null)}
                                    style={{ background: "#f5f5f7", color: "#1d1d1f", border: "none", borderRadius: "8px", padding: "8px 18px", fontWeight: 600, fontSize: "13px", cursor: "pointer" }}>
                                    Cancel
                                </button>
                                <button onClick={saveInsurance}
                                    disabled={confirmDraft.insuranceMemberId !== insuranceDraft.insuranceMemberId || confirmDraft.vaAuthorizationNumber !== insuranceDraft.vaAuthorizationNumber}
                                    style={{
                                        background: (confirmDraft.insuranceMemberId !== insuranceDraft.insuranceMemberId || confirmDraft.vaAuthorizationNumber !== insuranceDraft.vaAuthorizationNumber) ? "#a0c4e8" : "#0071e3",
                                        color: "#fff", border: "none", borderRadius: "8px", padding: "8px 18px", fontWeight: 600, fontSize: "13px", cursor: "pointer"
                                    }}>
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}